import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  read: number;
  created_at: number;
}

interface NotificationsResponse {
  unreadCount: number;
  notifications: Notification[];
}

function relativeTime(unixSeconds: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - unixSeconds;

  if (diff < 60) return "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h}h ago`;
  }
  if (diff < 604800) {
    const d = Math.floor(diff / 86400);
    return `${d}d ago`;
  }
  const date = new Date(unixSeconds * 1000);
  return date.toLocaleDateString();
}

const POLL_INTERVAL = 30_000;

export function NotificationBell() {
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiFetch<NotificationsResponse>("/notifications");
      setData(res);
    } catch {
      // Silently ignore fetch errors for polling
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.notifications ?? [];

  async function handleMarkAllRead() {
    try {
      await apiFetch("/notifications/read-all", { method: "POST" });
      setData((prev) =>
        prev
          ? {
              unreadCount: 0,
              notifications: prev.notifications.map((n) => ({ ...n, read: 1 })),
            }
          : prev,
      );
    } catch {
      // ignore
    }
  }

  async function handleClickNotification(notification: Notification) {
    // Mark as read if unread
    if (!notification.read) {
      try {
        await apiFetch(`/notifications/${notification.id}/read`, {
          method: "PATCH",
        });
        setData((prev) => {
          if (!prev) return prev;
          return {
            unreadCount: Math.max(0, prev.unreadCount - 1),
            notifications: prev.notifications.map((n) =>
              n.id === notification.id ? { ...n, read: 1 } : n,
            ),
          };
        });
      } catch {
        // ignore
      }
    }

    setOpen(false);

    if (notification.link) {
      navigate(notification.link);
    }
  }

  async function handleDelete(e: React.MouseEvent, notificationId: string) {
    e.stopPropagation();
    try {
      await apiFetch(`/notifications/${notificationId}`, {
        method: "DELETE",
      });
      setData((prev) => {
        if (!prev) return prev;
        const removed = prev.notifications.find((n) => n.id === notificationId);
        return {
          unreadCount:
            removed && !removed.read
              ? Math.max(0, prev.unreadCount - 1)
              : prev.unreadCount,
          notifications: prev.notifications.filter(
            (n) => n.id !== notificationId,
          ),
        };
      });
    } catch {
      // ignore
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-300 hover:text-gray-100"
        aria-label="Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[28rem] overflow-hidden rounded-lg bg-surface-card border border-white/10 shadow-xl z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-gray-100">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-brand-yellow hover:text-brand-gold transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClickNotification(n)}
                  className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-white/5 last:border-b-0 ${
                    n.read
                      ? "hover:bg-white/5"
                      : "bg-white/[0.03] hover:bg-white/[0.07]"
                  }`}
                >
                  {/* Unread dot */}
                  <div className="pt-1.5 shrink-0">
                    <span
                      className={`block h-2 w-2 rounded-full ${
                        n.read ? "bg-transparent" : "bg-brand-yellow"
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-200 truncate">
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                      {n.message}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      {relativeTime(n.created_at)}
                    </p>
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, n.id)}
                    className="shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all text-gray-500 hover:text-gray-300"
                    aria-label="Delete notification"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
